import { differenceInDays, startOfMonth, format } from "date-fns";
import type { MaintenanceReportData } from "@/hooks/use-fleet-reports";

export interface ProviderMetric {
  providerId: string;
  providerName: string;
  totalCost: number;
  avgCost: number;
  completionRate: number;
  avgRepairDays: number;
  totalOrders: number;
}

export interface MaintenanceTimelineEvent {
  date: string;
  type: 'maintenance' | 'alert';
  title: string;
  vehiclePlate: string;
  provider?: string;
  cost?: number;
  status: string;
  orderNumber?: number;
}

export interface MaintenanceRecommendation {
  category: 'cost' | 'efficiency' | 'safety' | 'provider';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  action: string;
  potentialSavings?: number;
  affectedVehicles?: number;
}

export interface MonthlyTrend {
  month: string;
  totalCost: number;
  totalOrders: number;
  avgCost: number;
  preventive: number;
  corrective: number;
}

export interface CostByType {
  preventive: { count: number; cost: number; percentage: number };
  corrective: { count: number; cost: number; percentage: number };
}

/**
 * Calculate provider performance metrics
 */
export function calculateProviderPerformance(
  workOrders: MaintenanceReportData[]
): ProviderMetric[] {
  const providerMap = new Map<string, {
    totalCost: number;
    orders: MaintenanceReportData[];
    completed: number;
  }>();

  workOrders.forEach(wo => {
    const providerName = wo.provider_name || 'Não informado';
    const key = `${wo.vehicle_id}:${providerName}`;

    if (!providerMap.has(key)) {
      providerMap.set(key, {
        totalCost: 0,
        orders: [],
        completed: 0
      });
    }

    const data = providerMap.get(key)!;
    data.totalCost += wo.cost;
    data.orders.push(wo);
    if (wo.status === 'completed') {
      data.completed++;
    }
  });

  const metrics: ProviderMetric[] = [];

  providerMap.forEach((data, key) => {
    const [vehicleId, providerName] = key.split(':');
    const totalOrders = data.orders.length;
    const avgCost = totalOrders > 0 ? data.totalCost / totalOrders : 0;
    const completionRate = totalOrders > 0 ? (data.completed / totalOrders) * 100 : 0;

    // Calculate average repair days
    let totalRepairDays = 0;
    let countWithDates = 0;
    
    data.orders.forEach(wo => {
      if (wo.opened_at && wo.closed_at) {
        const days = differenceInDays(new Date(wo.closed_at), new Date(wo.opened_at));
        totalRepairDays += days;
        countWithDates++;
      }
    });

    const avgRepairDays = countWithDates > 0 ? totalRepairDays / countWithDates : 0;

    metrics.push({
      providerId: vehicleId,
      providerName,
      totalCost: data.totalCost,
      avgCost,
      completionRate,
      avgRepairDays,
      totalOrders
    });
  });

  return metrics.sort((a, b) => b.totalCost - a.totalCost);
}

/**
 * Generate maintenance timeline
 */
export function generateMaintenanceTimeline(
  workOrders: MaintenanceReportData[]
): MaintenanceTimelineEvent[] {
  const events: MaintenanceTimelineEvent[] = [];

  workOrders.forEach(wo => {
    events.push({
      date: wo.opened_at,
      type: 'maintenance',
      title: wo.maintenance_type || 'Manutenção Avulsa',
      vehiclePlate: wo.vehicle_plate,
      provider: wo.provider_name || undefined,
      cost: wo.cost,
      status: wo.status,
      orderNumber: wo.order_number
    });
  });

  // Sort by date descending
  return events.sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );
}

/**
 * Calculate monthly trends
 */
export function calculateMonthlyTrends(
  workOrders: MaintenanceReportData[]
): MonthlyTrend[] {
  const monthMap = new Map<string, {
    totalCost: number;
    totalOrders: number;
    preventive: number;
    corrective: number;
  }>();

  workOrders.forEach(wo => {
    const monthKey = format(startOfMonth(new Date(wo.opened_at)), 'yyyy-MM');
    
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, {
        totalCost: 0,
        totalOrders: 0,
        preventive: 0,
        corrective: 0
      });
    }

    const data = monthMap.get(monthKey)!;
    data.totalCost += wo.cost;
    data.totalOrders++;

    // Assume preventive if has "preventiva" in maintenance_type, otherwise corrective
    if (wo.maintenance_type && wo.maintenance_type.toLowerCase().includes('preventiva')) {
      data.preventive++;
    } else {
      data.corrective++;
    }
  });

  const trends: MonthlyTrend[] = [];
  monthMap.forEach((data, monthKey) => {
    trends.push({
      month: format(new Date(monthKey), 'MMM/yy'),
      totalCost: data.totalCost,
      totalOrders: data.totalOrders,
      avgCost: data.totalOrders > 0 ? data.totalCost / data.totalOrders : 0,
      preventive: data.preventive,
      corrective: data.corrective
    });
  });

  return trends.sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * Calculate cost by maintenance type
 */
export function calculateCostByType(
  workOrders: MaintenanceReportData[]
): CostByType {
  let preventiveCost = 0;
  let preventiveCount = 0;
  let correctiveCost = 0;
  let correctiveCount = 0;

  workOrders.forEach(wo => {
    if (wo.maintenance_type && wo.maintenance_type.toLowerCase().includes('preventiva')) {
      preventiveCost += wo.cost;
      preventiveCount++;
    } else {
      correctiveCost += wo.cost;
      correctiveCount++;
    }
  });

  const totalCost = preventiveCost + correctiveCost;
  const preventivePercentage = totalCost > 0 ? (preventiveCost / totalCost) * 100 : 0;
  const correctivePercentage = totalCost > 0 ? (correctiveCost / totalCost) * 100 : 0;

  return {
    preventive: {
      count: preventiveCount,
      cost: preventiveCost,
      percentage: preventivePercentage
    },
    corrective: {
      count: correctiveCount,
      cost: correctiveCost,
      percentage: correctivePercentage
    }
  };
}

/**
 * Generate maintenance recommendations
 */
export function generateMaintenanceRecommendations(
  workOrders: MaintenanceReportData[],
  providerMetrics: ProviderMetric[]
): MaintenanceRecommendation[] {
  const recommendations: MaintenanceRecommendation[] = [];

  // Rule 1: High corrective maintenance ratio
  const costByType = calculateCostByType(workOrders);
  const correctiveRatio = costByType.corrective.percentage;

  if (correctiveRatio > 60) {
    const potentialSavings = costByType.corrective.cost * 0.35; // 35% reduction
    recommendations.push({
      category: 'cost',
      priority: 'high',
      title: 'Aumentar manutenções preventivas',
      description: `${correctiveRatio.toFixed(1)}% dos custos são com manutenções corretivas. Investir em preventiva pode reduzir custos em até 35%.`,
      action: 'Ver Planos Preventivos',
      potentialSavings
    });
  }

  // Rule 2: Provider with high costs and low completion rate
  const problematicProviders = providerMetrics.filter(p => 
    p.completionRate < 85 && p.totalOrders >= 3
  );

  if (problematicProviders.length > 0) {
    const provider = problematicProviders[0];
    recommendations.push({
      category: 'provider',
      priority: 'medium',
      title: 'Revisar fornecedor com baixa taxa de conclusão',
      description: `${provider.providerName} tem taxa de conclusão de ${provider.completionRate.toFixed(1)}%. Considere rever parceria.`,
      action: 'Comparar Fornecedores',
      potentialSavings: undefined
    });
  }

  // Rule 3: High average repair time
  const longRepairProviders = providerMetrics.filter(p => p.avgRepairDays > 5);
  if (longRepairProviders.length > 0) {
    recommendations.push({
      category: 'efficiency',
      priority: 'medium',
      title: 'Reduzir tempo de reparo',
      description: 'Alguns fornecedores têm tempo médio de reparo acima de 5 dias. Isso afeta a disponibilidade da frota.',
      action: 'Ver Análise de Tempo',
      potentialSavings: undefined
    });
  }

  // Rule 4: High total maintenance costs
  const avgCostPerOrder = workOrders.length > 0 
    ? workOrders.reduce((sum, wo) => sum + wo.cost, 0) / workOrders.length 
    : 0;

  if (avgCostPerOrder > 2000) {
    recommendations.push({
      category: 'cost',
      priority: 'medium',
      title: 'Custo médio por OS elevado',
      description: `Custo médio de R$ ${avgCostPerOrder.toFixed(2)} por ordem de serviço. Considere negociação com fornecedores.`,
      action: 'Analisar Custos',
      potentialSavings: workOrders.length * 200 // R$ 200 per order
    });
  }

  // Rule 5: Best provider recommendation
  const bestProvider = providerMetrics.find(p => 
    p.completionRate >= 95 && p.avgRepairDays <= 3 && p.totalOrders >= 5
  );

  if (bestProvider) {
    recommendations.push({
      category: 'provider',
      priority: 'low',
      title: 'Consolidar com fornecedor de excelência',
      description: `${bestProvider.providerName} demonstra excelente performance. Considere consolidar mais serviços.`,
      action: 'Ver Detalhes',
      potentialSavings: undefined
    });
  }

  return recommendations.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
}

/**
 * Identify critical vehicles needing attention
 */
export function identifyCriticalVehicles(
  workOrders: MaintenanceReportData[]
): Array<{
  vehicleId: string;
  vehiclePlate: string;
  maintenanceCount: number;
  totalCost: number;
  lastMaintenance: string;
}> {
  const vehicleMap = new Map<string, {
    count: number;
    cost: number;
    lastDate: string;
  }>();

  workOrders.forEach(wo => {
    const key = `${wo.vehicle_id}:${wo.vehicle_plate}`;
    
    if (!vehicleMap.has(key)) {
      vehicleMap.set(key, {
        count: 0,
        cost: 0,
        lastDate: wo.opened_at
      });
    }

    const data = vehicleMap.get(key)!;
    data.count++;
    data.cost += wo.cost;
    
    if (new Date(wo.opened_at) > new Date(data.lastDate)) {
      data.lastDate = wo.opened_at;
    }
  });

  const critical: Array<{
    vehicleId: string;
    vehiclePlate: string;
    maintenanceCount: number;
    totalCost: number;
    lastMaintenance: string;
  }> = [];

  vehicleMap.forEach((data, key) => {
    const [vehicleId, vehiclePlate] = key.split(':');
    
    // Critical if more than 8 maintenances or cost > 15000
    if (data.count > 8 || data.cost > 15000) {
      critical.push({
        vehicleId,
        vehiclePlate,
        maintenanceCount: data.count,
        totalCost: data.cost,
        lastMaintenance: data.lastDate
      });
    }
  });

  return critical.sort((a, b) => b.totalCost - a.totalCost);
}
